import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";

const dishes = ["stone-steak", "monkfish-prawn-skewer", "t-bone", "monkfish-curry", "garlic-prawns"];
const closeLabels = { en: "Close image", pt: "Fechar imagem", fr: "Fermer l’image", es: "Cerrar imagen" };

export const FoodHighlights = () => {
  const { t, lang } = useLang();
  const track = useRef(null);
  const dialog = useRef(null);
  const opener = useRef(null);
  const [selected, setSelected] = useState(null);
  const isOpen = selected !== null;
  const navigate = (direction) => setSelected(index => (index + direction + dishes.length) % dishes.length);

  useEffect(() => {
    if (!isOpen) return;
    const modal = dialog.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    modal.showModal();
    return () => {
      modal.close();
      document.body.style.overflow = previousOverflow;
      opener.current?.focus({ preventScroll: true });
    };
  }, [isOpen]);

  const scroll = (direction) => {
    const el = track.current;
    const item = el?.firstElementChild;
    if (item) el.scrollBy({ left: direction * (item.getBoundingClientRect().width + 24), behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };
  return (
    <section id="food" className="food-section section-pad" aria-labelledby="food-title">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <Reveal>
          <span className="ornament">{t.food.overline}</span>
          <h2 id="food-title" className="mt-6 font-serif text-4xl md:text-6xl font-light leading-tight">{t.food.title}</h2>
          <p className="mt-4 text-base md:text-lg text-[var(--ms-ink)]">{t.food.subtitle}</p>
        </Reveal>
        <div className="flex items-center justify-between gap-4 mt-8 lg:hidden">
          <p className="text-xs">{t.food.swipe}</p>
          <div className="flex gap-2">
            <button type="button" className="food-scroll-button" onClick={() => scroll(-1)} aria-label={t.food.previous} aria-controls="food-track"><ArrowLeft aria-hidden="true" size={18} /></button>
            <button type="button" className="food-scroll-button" onClick={() => scroll(1)} aria-label={t.food.next} aria-controls="food-track"><ArrowRight aria-hidden="true" size={18} /></button>
          </div>
        </div>
        <div id="food-track" ref={track} className="food-track" role="region" aria-label={t.food.title} tabIndex={0}>
          {dishes.map((dish, index) => (
            <article key={dish} className={`food-feature food-feature-${index + 1}`} aria-labelledby={`food-${dish}`}>
              <button type="button" className="food-image" aria-haspopup="dialog" aria-label={t.food.dishes[dish]} onClick={event => { opener.current = event.currentTarget; setSelected(index); }}>
                <img src={`/food/${dish}.png`} alt={t.food.dishes[dish]} loading="lazy" decoding="async" />
              </button>
              <div className="food-caption"><span aria-hidden="true" className="text-xs tracking-widest text-[var(--ms-gold-dark)]">0{index + 1}</span><h3 id={`food-${dish}`} className="font-serif text-2xl md:text-3xl leading-tight">{t.food.dishes[dish]}</h3></div>
            </article>
          ))}
        </div>
        <a href="#full-menu" className="mt-10 inline-flex items-center gap-4 py-3 border-b border-[var(--ms-gold)] text-xs font-semibold tracking-widest uppercase">{t.food.cta}<ArrowRight size={16} aria-hidden="true" /></a>
      </div>
      {isOpen && createPortal(
        <dialog ref={dialog} className="food-lightbox" aria-modal="true" aria-labelledby="food-lightbox-name"
          onCancel={() => setSelected(null)}
          onClose={() => setSelected(null)}
          onClick={event => { if (!event.target.closest("button, img, figcaption")) setSelected(null); }}
          onKeyDown={event => {
            if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
              event.preventDefault();
              navigate(event.key === "ArrowLeft" ? -1 : 1);
            }
          }}>
          <button type="button" autoFocus className="food-lightbox-close food-lightbox-control" aria-label={closeLabels[lang] || closeLabels.en} onClick={() => setSelected(null)}><X aria-hidden="true" size={24} /></button>
          <figure className="food-lightbox-figure">
            <img key={dishes[selected]} src={`/food/${dishes[selected]}.png`} alt={t.food.dishes[dishes[selected]]} decoding="async" />
            <figcaption id="food-lightbox-name" aria-live="polite" className="font-serif">{t.food.dishes[dishes[selected]]}</figcaption>
          </figure>
          <div className="food-lightbox-navigation">
            <button type="button" className="food-lightbox-control" aria-label={t.food.previous} onClick={() => navigate(-1)}><ArrowLeft aria-hidden="true" size={22} /></button>
            <span aria-hidden="true">{selected + 1} / {dishes.length}</span>
            <button type="button" className="food-lightbox-control" aria-label={t.food.next} onClick={() => navigate(1)}><ArrowRight aria-hidden="true" size={22} /></button>
          </div>
        </dialog>, document.body
      )}
    </section>
  );
};
